import React, { createContext, useContext, useRef, useState } from 'react';

import {
  GetMeDocument,
  useCompleteWebTutorialFlowsMutation,
  WebTutorialFlow,
} from '@graphql/generated';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

type ProjectTutorialWebType = {
  PROJECT: string;
  TASK: string;
  INVITE_MEMBER: string;
  CHAT: string;
  TAG: string;
  EXPAND_COLLAPSE: string;
};

type TutorialWebContextType = {
  isVisible: boolean;
  projectTutorialWebState: any;

  showOverlay: boolean;

  setShowOverlay: React.Dispatch<React.SetStateAction<boolean>>;

  setProjectTutorialWebState: React.Dispatch<React.SetStateAction<any>>;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setProjectTutorialWebStateAndInStorage: (
    state: ProjectTutorialWebType
  ) => void;
};

const ProjectTutorialWebContext = createContext<
  TutorialWebContextType | undefined
>(undefined);

export const ProjectTutorialWebProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const { trackEvent } = useMixpanel();
  const hasTrackedOnboardingStart = useRef(false);

  const [projectTutorialWebState, setProjectTutorialWebState] =
    useState<ProjectTutorialWebType>();

  const [completeWebTutorialFlows] = useCompleteWebTutorialFlowsMutation();

  const setProjectTutorialWebStateAndInStorage = async (
    state: ProjectTutorialWebType
  ) => {
    let flowType = null;
    let changedFlow: string | null = null;

    if (projectTutorialWebState) {
      if (projectTutorialWebState?.PROJECT !== state.PROJECT) {
        flowType = WebTutorialFlow.ProjectCreation;
        changedFlow = 'project';
      } else if (projectTutorialWebState?.TASK !== state.TASK) {
        flowType = WebTutorialFlow.TaskCreation;
        changedFlow = 'task';
      } else if (
        projectTutorialWebState?.INVITE_MEMBER !== state.INVITE_MEMBER
      ) {
        flowType = WebTutorialFlow.InviteMember;
        changedFlow = 'invite_member';
      } else if (projectTutorialWebState?.CHAT !== state.CHAT) {
        flowType = WebTutorialFlow.Chat;
        changedFlow = 'chat';
      } else if (projectTutorialWebState?.TAG !== state.TAG) {
        flowType = WebTutorialFlow.TagOnChatPanelOpen;
        changedFlow = 'tag';
      } else if (
        projectTutorialWebState?.EXPAND_COLLAPSE !== state.EXPAND_COLLAPSE
      ) {
        flowType = WebTutorialFlow.ExpandCollapse;
        changedFlow = 'expand_collapse';
      }
    } else if (!hasTrackedOnboardingStart.current) {
      hasTrackedOnboardingStart.current = true;
      trackEvent(MIXPANEL_EVENTS.ONBOARDING_STARTED, {
        platform: 'web',
        tutorial_type: 'project',
      });
    }

    if (changedFlow) {
      trackEvent(MIXPANEL_EVENTS.ONBOARDING_STEP_COMPLETED, {
        platform: 'web',
        tutorial_type: 'project',
        flow: changedFlow,
      });

      if (changedFlow === 'expand_collapse') {
        trackEvent(MIXPANEL_EVENTS.ONBOARDING_COMPLETED, {
          platform: 'web',
          tutorial_type: 'project',
          final_state: changedFlow,
        });
      }
    }

    setProjectTutorialWebState(state);
    await AsyncStorage.setItem(
      'projectTutorialWebState',
      JSON.stringify(state)
    );

    if (flowType !== null) {
      completeWebTutorialFlows({
        variables: {
          flowType,
        },
        refetchQueries: [{ query: GetMeDocument }, 'getMe'],
      });

      if (flowType === WebTutorialFlow.TagOnChatPanelOpen) {
        completeWebTutorialFlows({
          variables: {
            flowType: WebTutorialFlow.TagOnChatPanelClosed,
          },
          refetchQueries: [{ query: GetMeDocument }, 'getMe'],
        });
      }
    }
  };

  return (
    <ProjectTutorialWebContext.Provider
      value={{
        isVisible,
        setIsVisible,
        showOverlay,
        setShowOverlay,
        setProjectTutorialWebStateAndInStorage,
        projectTutorialWebState,
        setProjectTutorialWebState,
      }}>
      {children}
    </ProjectTutorialWebContext.Provider>
  );
};

// Hook to use context
export function useProjectTutorialWeb() {
  const context = useContext(ProjectTutorialWebContext);
  if (context === undefined) {
    throw new Error(
      'useProjectTutorialWeb must be used within a ProjectTutorialWebProvider'
    );
  }
  return context;
}
